import React from 'react';

import {
  Collaborator,
  PolicyType,
  convertCollaborationsToCollaborators,
  convertCollaboratorToCollaborations,
  isSchemaPolicyActorCollaborator,
  isSchemaPolicyGroupCollaborator,
} from 'auth/PermissionConstants';
import {SchemaPolicy} from 'auth/PolicyConstants';
import BenchlingConfig from 'BenchlingConfig';
import CollaborationManagementContent from 'collaborator-components/CollaborationManagementContent';
import CollaboratorsSection, {UpdateArgs} from 'collaborator-components/CollaboratorsSection';
import {Collaboration} from 'collaborator-components/useCollaborationManagementState';
import useSchemaPolicyListing from 'collaborator-components/useSchemaPoliciesListing';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import deepCopy from 'lib/deepCopy';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export interface SchemaCollaboratorsSectionProps {
  policyPredicate?: (policy: SchemaPolicy) => boolean;
  policeableId?: string;
  isReadOnly: boolean;
  collaborators: Array<Collaborator>;
  description?: JSX.Element;
  onUpdate: (collaborators: Array<Collaborator>) => void;
  onSubmit?: () => Promise<void>;
}

function _getAdminSchemaPolicyId(policies: Array<SchemaPolicy>): string {
  if (policies.length > 0) {
    return policies[0].id;
  }
  return '';
}

function _getDefaultSchemaPolicyId(policies: Array<SchemaPolicy>): string {
  if (policies.length > 0) {
    return policies[0].id;
  }
  return '';
}

function _dedupeCollaborators(
  collaborators: Array<Collaborator>,
  policies: Array<SchemaPolicy>
): Array<Collaborator> {
  const newCollaborators: Array<Collaborator> = [];
  for (const collaborator of collaborators) {
    if (isSchemaPolicyActorCollaborator(collaborator)) {
      const existingUserCollaborator = newCollaborators.find((c) => c.id === collaborator.id);
      if (existingUserCollaborator && isSchemaPolicyActorCollaborator(existingUserCollaborator)) {
        const existingPolicyIndex = policies.findIndex(
          (p) => p.id === existingUserCollaborator.schemaPolicyId
        );
        const newPolicyIndex = policies.findIndex((p) => p.id === collaborator.schemaPolicyId);
        if (newPolicyIndex > existingPolicyIndex) {
          existingUserCollaborator.schemaPolicyId = collaborator.schemaPolicyId;
        }
        continue;
      }
      newCollaborators.push(deepCopy(collaborator));
    } else {
      newCollaborators.push(collaborator);
    }
  }

  return newCollaborators;
}

function _setGroupOwnerAdminPolicyIfNull(collaborator: Collaborator): Collaborator {
  // This is a legacy workaround from the transition from CollaboratorsSection to CollaborationManagementContent.
  //
  // Schemas allow editing both the admin and member policy for group owners.
  // CollaborationManagementContent doesn't show the 'Add Admin Policy' option for group owners that only
  // have a member admin policy, and we don't want to special-case this for schemas.
  // Instead, if a group owner has a member policy but not an admin policy, we copy the member
  // policy over to admins to make sure the UI treats this as two separate collaborations.
  if (
    collaborator.owner &&
    isSchemaPolicyGroupCollaborator(collaborator) &&
    collaborator.adminSchemaPolicyId == null
  ) {
    return {
      ...deepCopy(collaborator),
      adminSchemaPolicyId: collaborator.memberSchemaPolicyId,
    };
  }

  return collaborator;
}

export default function SchemaCollaboratorsSection(
  props: SchemaCollaboratorsSectionProps
): JSX.Element | null {
  const [canSave, setCanSave] = React.useState<boolean>(true);
  function _handleSubmit(): void {
    if (!props.onSubmit) {
      return;
    }
    props
      .onSubmit()
      .then(() => {
        notifications.success(`Successfully updated collaborators.`);
      })
      .catch(notifications.notifyError);
  }

  function _handleUpdate(data: UpdateArgs): void {
    if (data.collaborators) {
      props.onUpdate(data.collaborators);
    }
  }

  function renderDescription(): JSX.Element {
    const defaultDescription = (
      <>
        <strong>All users</strong> can view objects of schemas by default. Granting additional access policies
        will control where schemas are listed in the product, who can create and edit schema objects, and who
        can manage schema access.{' '}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://help.benchling.com/hc/articles/9684210957581"
        >
          Learn more
        </a>
      </>
    );

    return <div className="u-spacing-bottom">{props.description || defaultDescription}</div>;
  }
  const result = useSchemaPolicyListing();

  if (result.queryResultType === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.queryResultType !== QueryResultType.SUCCESS) {
    // QueryResultType.ERROR or QueryResultType.SKIPPED
    notifications.error('Could not load schema policies; try reloading the page.');
    return null;
  } else {
    let schemaPolicies = result.schemaPolicies;
    if (props.policyPredicate != null) {
      schemaPolicies = schemaPolicies.filter((policy: SchemaPolicy) => props.policyPredicate?.(policy));
    }

    return (
      <div>
        {BenchlingConfig.PERMISSIONS_ENABLE_NEW_COLLABORATION_UI ? (
          <>
            {renderDescription()}
            <div className="u-large-padding-bottom">
              <CollaborationManagementContent
                isReadOnly={props.isReadOnly}
                collaborations={_dedupeCollaborators(props.collaborators, schemaPolicies).flatMap(
                  (collaborator) =>
                    convertCollaboratorToCollaborations(
                      _setGroupOwnerAdminPolicyIfNull(collaborator),
                      schemaPolicies
                    )
                )}
                ancestorCollaborations={[]}
                setCollaborations={(collaborations: Array<Collaboration>) => {
                  const collaborators = convertCollaborationsToCollaborators(collaborations);
                  props.onUpdate(collaborators);
                }}
                policies={schemaPolicies}
                allowEditableOwner={true}
                setCanSave={setCanSave}
              />
            </div>
          </>
        ) : (
          <CollaboratorsSection
            getAdminPolicyId={_getAdminSchemaPolicyId}
            getDefaultPolicyId={_getDefaultSchemaPolicyId}
            collaborators={_dedupeCollaborators(props.collaborators, schemaPolicies)}
            renderDescription={renderDescription}
            enableAuditors={false}
            isReadOnly={props.isReadOnly}
            policeableId={props.policeableId}
            onUpdate={_handleUpdate}
            policies={schemaPolicies}
            policyType={PolicyType.SCHEMA}
          />
        )}
        {!props.isReadOnly && props.onSubmit && (
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            disabled={!canSave}
            onClick={_handleSubmit}
            size={ButtonSize.MEDIUM}
            text="Update Collaborators"
            dataTestAttrs={{component: 'SchemaCollaboratorsSection', element: 'updateCollaboratorsButton'}}
          />
        )}
      </div>
    );
  }
}
